import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';

import debounce from 'lodash/debounce';
import { CircularProgress } from '@mui/material';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import ReportFilters from 'components/ReportFilters/ReportFilters';
import VerticalBarChart from 'examples/Charts/BarCharts/VerticalBarChart';
import PieChart from 'examples/Charts/PieChart';

import DataTable from 'examples/Tables/DataTable';

import {
  usersAcquiredColumns,
  usersByAgeColumns,
  usersByStateColumns,
} from '../../../constants/columns.constants';
import { usersAcquiredRows, usersByAgeRows, usersByStateRows } from './Rows';

import useReportsHook from 'hooks/useReportsHook';
import usePaginationHook from 'hooks/usePaginationHook';

import { formatStringToNumber } from 'helpers/formatData';
import { COLORS } from 'constants/colors.constants';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const UsersReportsTab = ({ reportType }) => {
  const [selectedDuration, setSelectedDuration] = useState(null);

  const {
    currentPage,
    setCurrentPage,
    perPage,
    setPerPage,
    sortBy,
    setSortBy,
    searchTerm,
  } = usePaginationHook();

  const {
    fetchReportsData,
    reportsList,
    isLoading,
    getTabTitle,
    isFetchingChart = false,
    chartResponse = [],
    fetchUserAcquiredCharts,
    fetchUserStatesCharts,
    statesDataChart = null,
  } = useReportsHook({
    currentPage,
    perPage,
    sortBy,
    searchTerm,
    reportType,
    duration: selectedDuration,
  });

  const columns = useMemo(() => {
    switch (reportType) {
      case 'users-acquired':
        return usersAcquiredColumns;

      case 'users-by-state':
        return usersByStateColumns;

      case 'users-by-age':
        return usersByAgeColumns;

      default:
        return [];
    }
  }, [reportType]);

  const rows = useMemo(() => {
    switch (reportType) {
      case 'users-acquired':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(usersAcquiredRows)
          : [];

      case 'users-by-state':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(usersByStateRows)
          : [];

      case 'users-by-age':
        return reportsList?.reportsData?.length > 0
          ? reportsList?.reportsData?.map(usersByAgeRows)
          : [];

      default:
        return [];
    }
  }, [reportType, reportsList?.reportsData]);

  const onFetchDataDebounced = debounce(fetchReportsData, 0);

  useEffect(() => {
    switch (reportType) {
      case 'users-acquired':
        fetchUserAcquiredCharts();
        break;

      case 'users-by-state':
        fetchUserStatesCharts();
        break;

      default:
        break;
    }

    return () => {};
  }, [reportType]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDuration]);

  useEffect(() => {
    reportType === 'users-by-state' && setSortBy({ id: 'state', desc: true });
  }, [reportType]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, perPage, searchTerm, selectedDuration]);

  const getRandomColor = (totalCount) => {
    const colors = [];
    for (let i = 0; i < totalCount; i++) {
      colors.push('#' + Math.floor(Math.random() * 16777215).toString(16));
    }
    return colors;
  };

  const onChangeDuration = (value) => {
    setSelectedDuration(value);
  };

  const ChartLoader = () => (
    <MDBox
      sx={{
        width: '100%',
        minHeight: '200px',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <CircularProgress color="info" />
    </MDBox>
  );

  return (
    <>
      <MDBox
        mx={2}
        mt={3}
        py={3}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          {getTabTitle()}
        </MDTypography>
      </MDBox>
      <MDBox mt={5} px={2}>
        {isFetchingChart ? (
          <ChartLoader />
        ) : (
          <>
            {reportType === 'users-acquired' && (
              <VerticalBarChart
                icon={{ color: 'info', component: 'leaderboard' }}
                title={getTabTitle()}
                chart={{
                  labels: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Apr',
                    'May',
                    'Jun',
                    'Jul',
                    'Aug',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                  ],
                  datasets: [
                    {
                      label: getTabTitle(),
                      color: 'dark',
                      data: chartResponse,
                    },
                  ],
                }}
              />
            )}

            {reportType === 'users-by-state' && (
              <PieChart
                icon={{ color: 'info', component: 'leaderboard' }}
                title="User by States"
                chart={{
                  labels: statesDataChart?.map((data) => data?.state) ?? [],
                  datasets: {
                    label: 'Users',
                    backgroundColors: COLORS,
                    data: statesDataChart?.map((data) => data?.userCount) ?? [],
                  },
                }}
                legend={true}
              />
            )}
          </>
        )}

        {isLoading && reportType === 'users-by-age' ? (
          <ChartLoader />
        ) : (
          <>
            {reportType === 'users-by-age' && (
              <PieChart
                icon={{ color: 'info', component: 'leaderboard' }}
                title="User by Age Group"
                chart={{
                  labels:
                    reportsList?.reportsData?.map((row) => row?.ageGroup) ?? [],
                  datasets: {
                    label: 'Users',
                    backgroundColors: [
                      'red',
                      'orange',
                      'green',
                      'teal',
                      'yellow',
                      'lime',
                      'blue',
                      'purple',
                    ],
                    data:
                      reportsList?.reportsData?.map((row) =>
                        formatStringToNumber(row?.users ?? 0)
                      ) ?? [],
                  },
                }}
                legend={true}
              />
            )}{' '}
          </>
        )}
      </MDBox>
      <ReportFilters
        onChangeDuration={onChangeDuration}
        reportType={reportType}
      />
      <MDBox
        pt={3}
        sx={{
          position: 'relative',
          paddingTop: '85px',
          paddingBottom: {
            xs: '150px',
            sm: '95px',
          },
        }}
      >
        <DataTable
          table={{ columns, rows: rows }}
          isSorted={true}
          entriesPerPage={{ defaultValue: perPage }}
          showTotalEntries
          canSearch={false}
          // searchTerm={searchTerm}
          // setSearchTerm={setSearchTerm}
          perPage={perPage}
          setPerPage={setPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPage={reportsList?.totalPage}
          totalRecords={reportsList?.totalCount}
          // hiddenColumns={['id']}
          queryPageSortBy={[sortBy]}
          setSortBy={setSortBy}
          isLoading={isLoading}
        />
      </MDBox>
    </>
  );
};

UsersReportsTab.propTypes = {
  reportType: PropTypes.string,
};

UsersReportsTab.defaultValue = {
  reportType: '',
};

export default UsersReportsTab;
